// Cliente Service - Servicios API para el portal de clientes (JWT usuario, no admin)
import { api } from './api';
import type { ClienteContractResponse, ClientePerfilResponse } from '@/types/contracts';
import type { ClienteFacturasListResponse } from '@/types/clienteFactura';

export interface UpdateProfileRequest {
  full_name?: string;
  phone_number?: string;
}

export interface BillingPortalResponse {
  url: string;
}

/** Re-exportar para compatibilidad */
export type ClientProfile = ClientePerfilResponse;

export const clienteService = {
  /**
   * Obtener lista de contratos del cliente autenticado
   * Endpoint: GET /api/clientes/contratos (JWT usuario)
   */
  async getContratos(): Promise<ClienteContractResponse[]> {
    const { data } = await api.get<ClienteContractResponse[]>('/clientes/contratos');
    return data;
  },

  /**
   * Obtener facturas emitidas del cliente autenticado
   * Endpoint: GET /api/clientes/facturas (JWT usuario)
   */
  async getFacturas(): Promise<ClienteFacturasListResponse> {
    const { data } = await api.get<ClienteFacturasListResponse>('/clientes/facturas', {
      params: { skip: 0, limit: 50 },
    });
    return data;
  },

  /**
   * Descargar PDF de una factura del cliente (patrón blob de billingService.downloadQuotePdf)
   * Endpoint: GET /api/clientes/facturas/{id}/pdf
   * Si el backend responde 302 a un PDF local, el navegador lo sigue transparente.
   * Si falla (404/CORS), axios lanza y la UI muestra el error.
   */
  async downloadFacturaPdf(facturaId: string, invoiceNumber?: string): Promise<void> {
    const response = await api.get(`/clientes/facturas/${facturaId}/pdf`, {
      responseType: 'arraybuffer',
    });
    const blob = new Blob([response.data as ArrayBuffer], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const disposition = (response.headers as Record<string, string>)['content-disposition'] || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    link.download = match ? match[1] : `factura_${invoiceNumber ?? facturaId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  },

  /**
   * Crear sesión de Stripe Billing Portal
   * Endpoint: POST /api/clientes/billing-portal
   */
  async createBillingPortalSession(returnUrl?: string): Promise<BillingPortalResponse> {
    const { data } = await api.post<BillingPortalResponse>('/clientes/billing-portal', {
      return_url: returnUrl || window.location.href,
    });
    return data;
  },

  /**
   * Obtener perfil del cliente
   * Endpoint: GET /api/clientes/perfil
   */
  async getPerfil(): Promise<ClientePerfilResponse> {
    const { data } = await api.get<ClientePerfilResponse>('/clientes/perfil');
    return data;
  },

  /**
   * Actualizar perfil del cliente
   * Endpoint: PATCH /api/clientes/perfil
   */
  async updatePerfil(updates: UpdateProfileRequest): Promise<ClientePerfilResponse> {
    const { data } = await api.patch<ClientePerfilResponse>('/clientes/perfil', updates);
    return data;
  },
};
